import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import LabeledSheet from 'LuxKit/LabeledSheet'
import SolidShieldCheckIcon from 'LuxKit/Icons/solid/SolidShieldCheckIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import FlashCancellationPolicies from 'components/Common/CancellationPolicies/Flash/FlashCancellationPolicies'
import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import TextButton from 'LuxKit/Button/TextButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Heading from 'LuxKit/Typography/Heading'
import { mediaQueryDown } from 'components/utils/breakpoint'
import { useIntl } from 'react-intl'
import useRebrandSuccessDarkColour from 'hooks/useRebrandSuccessDarkColour'
import SuggestedOptionPreviewPriceBlock from './SuggestedOptionPreviewPriceBlock'
import SuggestedPackagePromotionLabel from './SuggestedPackagePromotionLabel'
import SuggestedOptionPreviewRoomImage from './SuggestedOptionPreviewRoomImage'
import type { SuggestedOptionPreviewViewProps } from './suggestedOptionPreviewTypes'

const Body = styled(Group)`
  padding: ${rem(24)} ${rem(20)};
`

const ContentContainer = styled.div`
  flex: 1;
  min-width: 0;
  container-type: inline-size;
`

const Content = styled.div`
  display: flex;
  gap: ${rem(20)};
  flex-direction: column;

  @container (min-width: 410px) {
    flex-direction: row;
    align-items: start;
    justify-content: space-between;
  }

  ${mediaQueryDown.mobile} {
    flex-direction: column;
    align-items: stretch;
  }
`

const DesktopImageContainer = styled.div`
  width: ${rem(120)};
  height: ${rem(120)};
  flex-shrink: 0;
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;

  ${mediaQueryDown.mobile} {
    display: none;
  }
`

const MobileRoomImageWrap = styled.div`
  width: ${rem(64)};
  height: ${rem(64)};
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
`

const DetailsColumn = styled.div`
  flex: 1;
  min-width: 0;
`

const PricingColumn = styled(Group)`
  width: ${rem(170)};
  flex-shrink: 0;

  ${mediaQueryDown.mobile} {
    width: 100%;
    align-self: stretch;
  }
`

function SuggestedOptionPreviewRebrand(props: SuggestedOptionPreviewViewProps) {
  const intl = useIntl()
  const cancellationIconColour = useRebrandSuccessDarkColour()
  const {
    message,
    roomName,
    roomImageId,
    offer,
    pkg,
    checkInDate,
    charterBundleRoute,
    charterDepartDate,
    allInclusions,
    onShowMoreInclusions,
    nightsText,
    rewardPriceData,
    needsRewardPreview,
    rewardPreview,
    pricingToShow,
    memberPricingToShow,
    convertToDisplay,
    priceMode,
    rewardUnitLabel,
    isSameRoomMode,
    onViewPackage,
    viewPackageLabel,
    onOpenRoomGallery,
    openRoomGalleryLabel,
  } = props

  const truncatedInclusionCount = Math.max(0, allInclusions.length - 2)
  const showMoreInclusionsText = intl.formatMessage(
    {
      id: 'eiaPF_tSKjiIYr_Q2Xk-',
      defaultMessage: '+{count} more inclusions',
      description: 'Link text showing how many additional inclusions are truncated on the rebrand suggested option preview tile',
    },
    { count: truncatedInclusionCount },
  )

  return <LabeledSheet
    kind="highlight"
    label={<SuggestedPackagePromotionLabel message={message} />}
  >
    <Body direction="horizontal" horizontalAlign="space-between" verticalAlign="start" gap={20}>
      <DesktopImageContainer>
        <SuggestedOptionPreviewRoomImage
          id={roomImageId}
          width={120}
          height={120}
          mobileWidth="120px"
          onOpenRoomGallery={onOpenRoomGallery}
          openRoomGalleryLabel={openRoomGalleryLabel}
        />
      </DesktopImageContainer>
      <ContentContainer>
        <Content>
          <DetailsColumn>
            <Group direction="vertical" gap={4}>
              <CSSBreakpoint max="mobile">
                <Group direction="horizontal" gap={12} verticalAlign="center">
                  <MobileRoomImageWrap>
                    {/* This tile is only mounted at desktop; keep the mobile image static. */}
                    <SuggestedOptionPreviewRoomImage
                      id={roomImageId}
                      width={64}
                      height={64}
                      mobileWidth="64px"
                    />
                  </MobileRoomImageWrap>
                  <Heading variant="heading4">{roomName}</Heading>
                </Group>
              </CSSBreakpoint>
              <CSSBreakpoint min="tablet">
                <BodyText variant="large" weight="bold">{roomName}</BodyText>
              </CSSBreakpoint>

              {offer.property && pkg.roomRate?.cancellationPolicy && (
                <FlashCancellationPolicies
                  checkInDate={checkInDate}
                  // Pass explicitly (even when undefined) so FlashCancellationPolicies does not
                  // fall back to a stale checkout-cart charter departure while dates resolve.
                  charterDepartDate={charterBundleRoute ? charterDepartDate : undefined}
                  propertyTimezoneOffset={offer.property.timezoneOffset}
                  cancellationPolicy={pkg.roomRate.cancellationPolicy}
                  leadingIcon={<SolidShieldCheckIcon size="S" colour={cancellationIconColour} />}
                  hideNonRefundable
                />
              )}

              {allInclusions.length > 0 && (
                <OfferInclusionsList
                  inclusions={allInclusions}
                  truncateAfter={2}
                  onShowMoreClick={onShowMoreInclusions}
                  showMoreText={showMoreInclusionsText}
                />
              )}
            </Group>
          </DetailsColumn>

          <PricingColumn direction="vertical" gap={16} verticalAlign="start">
            <Group direction="vertical" gap={8} horizontalAlign="start">
              <SuggestedOptionPreviewPriceBlock
                nightsText={nightsText}
                rewardPriceData={rewardPriceData}
                needsRewardPreview={needsRewardPreview}
                rewardPreview={rewardPreview}
                pricingToShow={pricingToShow}
                memberPricingToShow={memberPricingToShow}
                convertToDisplay={convertToDisplay}
                priceMode={priceMode}
                rewardUnitLabel={rewardUnitLabel}
                offerConfig={offer.luxPlus}
              />
            </Group>

            {isSameRoomMode && <TextButton
              kind="secondary"
              fit="mobile-full-width"
              onClick={onViewPackage}
            >
              {viewPackageLabel}
            </TextButton>}
          </PricingColumn>
        </Content>
      </ContentContainer>
    </Body>
  </LabeledSheet>
}

export default SuggestedOptionPreviewRebrand
